import { riskBand, riskScore, type RiskBand } from "./risk-score";

export { riskBand };

export const RISK_STATUSES = ["OPEN", "MITIGATED", "ACCEPTED", "CLOSED"] as const;
export type RiskLogStatus = (typeof RISK_STATUSES)[number];

export const RISK_LOG_EMPTY = "No risks";
export const RISK_LOG_UNAVAILABLE = "Unavailable";

export type RiskLogRow = {
  id: string;
  title: string;
  status: RiskLogStatus;
  probability: number;
  impact: number;
  score: number;
  band: RiskBand;
  ownerUserId: string | null;
};

export type RiskLogData =
  | { status: "unavailable" }
  | { status: "empty"; total: 0 }
  | {
      status: "ready";
      total: number;
      items: RiskLogRow[];
      /** Null when the payload is a page, not the full set. */
      heat: number[][] | null;
    };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isStatus(value: unknown): value is RiskLogStatus {
  return typeof value === "string" && (RISK_STATUSES as readonly string[]).includes(value);
}

function readRow(value: unknown): RiskLogRow | null {
  if (!isRecord(value)) return null;
  if (typeof value.id !== "string" || value.id.length === 0) return null;
  if (typeof value.title !== "string") return null;
  if (!isStatus(value.status)) return null;
  if (typeof value.probability !== "number" || typeof value.impact !== "number") return null;
  const score = riskScore(value.probability, value.impact);
  if (score === null) return null;
  const band = riskBand(score);
  if (band === null) return null;
  const owner = value.ownerUserId;
  return {
    id: value.id,
    title: value.title,
    status: value.status,
    probability: value.probability,
    impact: value.impact,
    score,
    band,
    ownerUserId: typeof owner === "string" && owner.length > 0 ? owner : null,
  };
}

export function heatMapFromRows(rows: RiskLogRow[]): number[][] {
  const grid = Array.from({ length: 5 }, () => Array.from({ length: 5 }, () => 0));
  for (const row of rows) {
    grid[row.probability - 1][row.impact - 1] += 1;
  }
  return grid;
}

/**
 * GET /projects/:id/risks is `{ data: { items, total }, meta }`.
 * The shared client turns a meta envelope into `{ __zephixInner, __zephixMeta }`.
 * Both shapes, and an already-unwrapped `{ items, total }`, are accepted.
 */
function riskListBody(payload: unknown): Record<string, unknown> | null {
  if (!isRecord(payload)) return null;
  if ("__zephixInner" in payload) return riskListBody(payload.__zephixInner);
  const data = payload.data;
  if (isRecord(data) && ("items" in data || "total" in data)) return data;
  return payload;
}

export function parseRiskList(payload: unknown): RiskLogData {
  const unavailable: RiskLogData = { status: "unavailable" };
  const body = riskListBody(payload);
  if (!body) return unavailable;
  const total = body.total;
  if (typeof total !== "number" || !Number.isInteger(total) || total < 0) return unavailable;
  if (!Array.isArray(body.items)) return unavailable;
  const items = body.items.map(readRow).filter((row): row is RiskLogRow => row !== null);
  if (total === 0) {
    return items.length === 0 ? { status: "empty", total: 0 } : unavailable;
  }
  return {
    status: "ready",
    total,
    items,
    heat: items.length === total ? heatMapFromRows(items) : null,
  };
}

export function rowsForCell(
  items: RiskLogRow[],
  probability: number,
  impact: number,
): RiskLogRow[] {
  return items.filter((row) => row.probability === probability && row.impact === impact);
}
